import { useAgGridRowListener } from "$root/third-party-integrations/ag-grid/ag-grid";
import { coalesce, getThemeCssVars } from "$root/utils";
import {
	CircularProgressBar,
	DebouncedSearchInput,
	Icon,
	Row,
	ScrollWrapper,
	Text,
	useListbox,
	type DebouncedSearchInputHandle,
} from "@mdotm/shadcn/legacy/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { toClassName, useRefProxyWithState } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { ForEach } from "@mdotm/utils/react-extra";
import type { ColDef, ColDefField } from "ag-grid-community";
import type { CustomCellEditorProps } from "ag-grid-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { tagColorMap } from "../../colors";
import type { ColumnOverrideParams } from "../../column/common-columns-definition";
import { type TagOptionsParams, type TagSettings, cellRendererWrapper, tooltipComponentWrapper } from "./base-cell";
import { TagBadge } from "./Tag";
import { Divider } from "$root/components/Divider";
import type { OptionModelDto } from "@eu.mdotm.api/sphere";

function readCellEditorTagParams(colDef: ColDef | undefined): TagOptionsParams {
	const params = colDef?.cellEditorParams as TagOptionsParams | undefined;
	return { options: params?.options ?? [], isLoading: params?.isLoading ?? false };
}

export function richTagColDef<T>(
	field: ColDefField<T, Array<string>>,
	meta: TagSettings,
	opt?: Omit<
		ColumnOverrideParams<T, Array<string>>,
		"tooltipComponent" | "cellRenderer" | "cellEditor" | "tooltipValueGetter" | "valueGetter"
	>,
): ColDef<T, Array<string>> {
	return {
		field,
		colId: meta.uuid,
		headerName: meta.name,
		suppressKeyboardEvent: (params) => params.editing,
		tooltipComponent: tooltipComponentWrapper(),
		cellRenderer: cellRendererWrapper(meta),
		cellEditor: richCellEditorWrapper(meta),
		editable: opt?.editable ?? true,
		cellEditorPopup: false,
		valueFormatter: (params) => (params.value ?? []).join(", "),
		suppressPaste: (params) => {
			const cellRendererParams = params.colDef.cellRendererParams as TagOptionsParams | undefined;
			return cellRendererParams?.isLoading ?? false;
		},
		valueParser: (params) => {
			const parsed = params.newValue
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);
			const cellRendererParams = params.colDef.cellRendererParams as TagOptionsParams | undefined;
			const options = cellRendererParams?.options ?? [];
			if (options.length === 0) {
				return parsed;
			}
			const validOptions = new Set(options.map((o) => o.option));
			return parsed.filter((s) => validOptions.has(s));
		},
		...coalesce(opt, {}),
	};
}

function getInitialOutlinedIndex(
	options: Array<Required<OptionModelDto>>,
	editableValues: Array<string>,
	multi: boolean | undefined,
): number | null {
	if (multi) {
		return null;
	}
	const selectedValue = editableValues.at(0);
	if (!selectedValue) {
		return null;
	}
	const index = options.findIndex((opt) => opt.option === selectedValue);
	return index === -1 ? null : index;
}

function richCellEditorWrapper<T>(meta: TagSettings) {
	return function RichTagCellEditor(row: CustomCellEditorProps<T, Array<string>>): React.JSX.Element {
		const { options, isLoading } = readCellEditorTagParams(row.colDef);
		const editableValuesRef = useRef<Array<string>>(row.value ?? []);
		const [editableValues, setEditableValues] = useRefProxyWithState<Array<string>>(row.value ?? [], editableValuesRef);
		const [searchQuery, setSearchQuery] = useState("");
		const searchHandleRef = useRef<DebouncedSearchInputHandle | null>(null);
		const [dropdownStyle, setDropdownStyle] = useState<{
			position: "fixed";
			top: number;
			left: number;
			width: number;
			zIndex: number;
		} | null>(null);

		// Portal into .ag-body-viewport so focus stays inside one of the grid's
		// registered focus viewports, preventing stopEditingWhenCellsLoseFocus from
		// triggering on dropdown clicks. AG-Grid's focusout check requires the focused
		// element to be inside eBodyViewport (.ag-body-viewport) — which contains both
		// the center and pinned (left/right) row containers. We must NOT go higher
		// (e.g. .ag-root-wrapper), as that is above the registered viewports and the
		// contains() check would fail for every cell. We must NOT use .ag-viewport,
		// as pinned cells have no .ag-viewport ancestor and would fall back outside.
		const portalTarget = useMemo(() => row.eGridCell?.closest(".ag-body-viewport") ?? document.body, [row.eGridCell]);

		useAgGridRowListener(row, "cellEditingStopped", () => {
			row.node.setDataValue(meta.uuid, editableValuesRef.current);
		});

		useLayoutEffect(() => {
			if (!row.eGridCell) {
				return;
			}
			const rect = row.eGridCell.getBoundingClientRect();
			setDropdownStyle({
				position: "fixed",
				top: rect.bottom,
				left: rect.left,
				width: Math.max(rect.width, 224),
				zIndex: 9999,
			});
		}, [row.eGridCell]);

		const extractFn = useCallback((o: Required<OptionModelDto>) => o.option, []);

		const { filtered: searchFiltered } = useSearchable({
			query: searchQuery,
			collection: options,
			extractFn,
		});

		const listboxOptions = useMemo(
			() => searchFiltered.map((o) => ({ label: o.option, value: o.option })),
			[searchFiltered],
		);

		const [outlinedItemIndex, setOutlinedItemIndex] = useState<number | null>(() =>
			getInitialOutlinedIndex(options, editableValues, meta.multi),
		);
		const [scrollAreaEl, setScrollAreaEl] = useState<HTMLDivElement | null>(null);

		// Scroll outlined item into view
		useEffect(() => {
			if (!scrollAreaEl || outlinedItemIndex === null) {
				return;
			}
			const el = scrollAreaEl.querySelector(`[data-listbox-index="${outlinedItemIndex}"]`) as HTMLElement | null;
			if (!el) {
				return;
			}
			const scrollBehaviorSnapshot = scrollAreaEl.style.scrollBehavior;
			scrollAreaEl.style.scrollBehavior = "";
			scrollAreaEl.scrollTop = Math.max(0, el.offsetTop - scrollAreaEl.clientHeight / 2);
			scrollAreaEl.style.scrollBehavior = scrollBehaviorSnapshot;
		}, [outlinedItemIndex, scrollAreaEl]);

		const toggleItem = (optionValue: string) => {
			setEditableValues((prev) => {
				if (meta.multi) {
					return prev.includes(optionValue) ? prev.filter((x) => x !== optionValue) : [...prev, optionValue];
				}
				return prev.includes(optionValue) ? [] : [optionValue];
			});

			if (!meta.multi) {
				row.eGridCell?.focus({ preventScroll: true });
				row.stopEditing();
			}
		};

		const { handleKeyDown } = useListbox({
			filteredOptions: listboxOptions,
			outlinedItemIndex,
			onOutlinedItemIndexChange: setOutlinedItemIndex,
			onOutlinedSelected: (index) => {
				const opt = searchFiltered[index];
				if (opt) {
					toggleItem(opt.option);
				}
			},
			onClose: () => {
				row.eGridCell?.focus({ preventScroll: true });
				row.stopEditing();
			},
			interceptCharacters: false,
			alwaysOutlineFirst: false,
		});

		if (isLoading) {
			return (
				<Row alignItems="center" justifyContent="center" className="h-full">
					<CircularProgressBar value="indeterminate" outerDiameter={16} innerDiameter={12} />
				</Row>
			);
		}

		return (
			<div className="h-full">
				<Row gap={4} alignItems="center" className="h-full px-2 overflow-x-auto">
					<ForEach collection={editableValues}>
						{(val) => {
							const opt = options.find((o) => o.option === val);
							return (
								<div role="none" onMouseDown={(e) => e.preventDefault()}>
									<TagBadge
										label={val}
										color={
											opt != null
												? (tagColorMap[opt.color]?.badgeColor() ?? getThemeCssVars(themeCSSVars.palette_N50))
												: getThemeCssVars(themeCSSVars.palette_N50)
										}
										onRemove={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setEditableValues((prev) => prev.filter((x) => x !== val));
										}}
									/>
								</div>
							);
						}}
					</ForEach>
				</Row>
				{dropdownStyle != null &&
					createPortal(
						<div style={dropdownStyle} className="bg-white rounded-sm border shadow-lg overflow-hidden">
							<DebouncedSearchInput
								externalQuery={searchQuery}
								onChange={setSearchQuery}
								handleRef={searchHandleRef}
								placeholder="Search..."
								autoFocus
								size="x-small"
								inputAppearance={{ className: "border-none" }}
								onKeyDown={handleKeyDown}
							/>
							<Divider direction="horizontal" />
							<ScrollWrapper direction="vertical" innerRef={setScrollAreaEl} className="max-h-[200px]">
								{searchFiltered.length === 0 ? (
									<Row alignItems="center" className="px-3 py-2">
										<Text type="Body/S/Book" color={themeCSSVars.palette_N400}>
											No options available
										</Text>
									</Row>
								) : (
									<ForEach collection={searchFiltered}>
										{(opt, index) => {
											const isAlreadySelected = editableValues.includes(opt.option);
											const isOutlined = outlinedItemIndex === index;
											return (
												<button
													type="button"
													data-listbox-index={index}
													aria-current={isOutlined}
													className={toClassName({
														"w-full text-left px-3 py-2 hover:bg-(--palette-N20) cursor-pointer relative": true,
														"bg-(--palette-N20)": isAlreadySelected,
														"bg-[#EFF0F3]": isOutlined && !isAlreadySelected,
													})}
													onMouseDown={(e) => e.preventDefault()}
													onClick={() => toggleItem(opt.option)}
												>
													<Row>
														<TagBadge
															label={opt.option}
															color={tagColorMap[opt.color]?.badgeColor() ?? getThemeCssVars(themeCSSVars.palette_N50)}
														/>
														<Icon
															icon="Outline"
															className={{
																"ml-auto absolute right-2 top-1/2 -translate-y-1/2": true,
																visible: isAlreadySelected,
																invisible: !isAlreadySelected,
															}}
															size={20}
															style={{ display: "inline-block" }}
														/>
													</Row>
												</button>
											);
										}}
									</ForEach>
								)}
							</ScrollWrapper>
						</div>,
						portalTarget,
					)}
			</div>
		);
	};
}
